import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Grid,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createInvoice, updateInvoice } from "../../services/invoiceService";
import { uploadAttachment } from "../../services/uploadService";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import {
  INVOICE_FORM_MODAL_SX,
  FORM_MODAL_CONTENT_SX,
  DENSE_FORM_BODY_SX,
  DENSE_FIELD_PROPS,
} from "../../utils/formModalStyles";
import { getOrganizationId, getOrganizationName } from "../../utils/organizationUtils";
import { useAuth } from "../../context/AuthContext";
import {
  InvoiceAttachmentControl,
  InvoiceSummarySection,
  calcInvoiceGstBreakdown,
  resolveAttachmentUrl,
} from "../../components/InvoiceFormShared";
import { useOrganizationGstPercentage } from "../../hooks/useOrganizationGstPercentage";

const createValidationSchema = Yup.object({
  invoiceType: Yup.string().required("Invoice type is required"),
  fromName: Yup.string().trim().required("From name is required"),
  amount: Yup.number()
    .required("Amount is required")
    .min(0, "Amount must be positive"),
  payment_mode: Yup.object()
    .nullable()
    .test("payment-mode", "Payment mode is required", (value) =>
      Boolean(value?.mode)
    ),
});

/** Edit mode: only attachment + remarks are editable. */
const editValidationSchema = Yup.object({});

const parsePaymentMode = (invoice) => {
  const mode = (invoice?.paymentMode || "").toString().toLowerCase();
  if (!mode) return { mode: "cash" };
  return {
    mode,
    organizationId: invoice?.creditOrganizationId || null,
    organizationName: null,
  };
};

const AddNewInvoice = ({ open, onClose, invoice_info }) => {
  const { user } = useAuth();
  const gstPercentage = useOrganizationGstPercentage(user, { enabled: open });
  const [attachment, setAttachment] = useState(null);

  // Locked for this modal instance (parent remounts via key on open)
  const isEditMode = Boolean(invoice_info?.id);
  const modalTitle = isEditMode ? "Edit Invoice" : "Create Invoice";

  const calculateBaseAmount = (totalAmount, sgst, cgst) => {
    if (invoice_info?.id && totalAmount && (sgst || cgst)) {
      const totalTax = (sgst || 0) + (cgst || 0);
      if (totalTax > 0) {
        return totalAmount - totalTax;
      }
    }
    return 0;
  };

  const getHospitalOrganizationName = () => {
    return getOrganizationName(user) || "Aryan Hospital";
  };

  const formik = useFormik({
    initialValues: {
      invoiceType: invoice_info?.invoiceType || "",
      fromName: invoice_info?.fromName || "",
      amount:
        invoice_info?.amount ||
        calculateBaseAmount(
          invoice_info?.totalAmount,
          invoice_info?.sgst,
          invoice_info?.cgst
        ) ||
        0,
      totalAmount: invoice_info?.totalAmount || 0,
      sgst: invoice_info?.sgst || 0,
      cgst: invoice_info?.cgst || 0,
      attachment: invoice_info?.attachment || "",
      particulars: invoice_info?.particulars || invoice_info?.remarks || "",
      payment_mode: parsePaymentMode(invoice_info),
    },
    enableReinitialize: false,
    validationSchema: isEditMode ? editValidationSchema : createValidationSchema,
    onSubmit: async (values) => {
      try {
        const subtotal = values.amount || 0;
        let finalSgst = values.sgst || 0;
        let finalCgst = values.cgst || 0;
        let finalTotalAmount = values.totalAmount || 0;

        if (!isEditMode) {
          const gstAmount =
            subtotal === 0 ? 0 : subtotal * (gstPercentage / 100);
          finalSgst = subtotal === 0 ? 0 : gstAmount / 2;
          finalCgst = subtotal === 0 ? 0 : gstAmount / 2;
          finalTotalAmount = subtotal + gstAmount;
        }

        let hospitalOrgId = getOrganizationId(user);
        if (!hospitalOrgId) {
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              const orgId = parsedUser?.organizationId;
              if (
                orgId != null &&
                orgId !== "" &&
                !isNaN(orgId) &&
                Number(orgId) > 0
              ) {
                hospitalOrgId = Number(orgId);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }

        const paymentModeValue = values.payment_mode;
        const paymentMode = (paymentModeValue?.mode || "cash").toUpperCase();
        const creditOrganizationId =
          paymentMode === "CREDIT"
            ? paymentModeValue?.organizationId || null
            : null;

        const hospitalName = getHospitalOrganizationName();

        if (isEditMode) {
          // Edit: only attachment + remarks (particulars) may change
          let nextAttachment = resolveAttachmentUrl(invoice_info.attachment);

          if (attachment && attachment instanceof File) {
            const uploaded = await uploadAttachment(attachment);
            const uploadedUrl = resolveAttachmentUrl(uploaded);
            if (uploadedUrl) {
              nextAttachment = uploadedUrl;
            }
          } else if (!values.attachment) {
            nextAttachment = null;
          } else {
            // Keep existing URL when form still has attachment text/url
            nextAttachment =
              resolveAttachmentUrl(values.attachment) ||
              resolveAttachmentUrl(invoice_info.attachment);
          }

          const updateData = {
            ...invoice_info,
            particulars: values.particulars?.trim() || "",
            attachment: nextAttachment,
          };

          delete updateData.id;
          delete updateData.createdAt;
          delete updateData.updatedAt;
          delete updateData.payment_mode;

          Object.keys(updateData).forEach((key) => {
            if (Array.isArray(updateData[key])) {
              delete updateData[key];
            }
          });

          const updated = await updateInvoice(invoice_info.id, updateData);
          handelCloseModalState({
            ...(updated && typeof updated === "object" ? updated : {}),
            id: invoice_info.id,
            particulars: values.particulars?.trim() || "",
            attachment:
              resolveAttachmentUrl(updated?.attachment) ||
              nextAttachment ||
              null,
          });
        } else {
          const invoiceData = {
            invoiceType: values.invoiceType,
            fromName: values.fromName.trim(),
            toName: hospitalName,
            amount: subtotal,
            sgst: finalSgst,
            cgst: finalCgst,
            totalAmount: finalTotalAmount,
            particulars: values.particulars?.trim() || "",
            paymentMode,
            creditOrganizationId,
            organizationId: hospitalOrgId,
          };

          if (attachment && attachment instanceof File) {
            const uploaded = await uploadAttachment(attachment);
            const uploadedUrl = resolveAttachmentUrl(uploaded);
            if (uploadedUrl) {
              invoiceData.attachment = uploadedUrl;
            }
          }

          const created = await createInvoice(invoiceData);
          handelCloseModalState({
            ...(created && typeof created === "object" ? created : {}),
            id: created?.id,
            particulars: values.particulars?.trim() || "",
            attachment:
              resolveAttachmentUrl(created?.attachment) ||
              invoiceData.attachment ||
              null,
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
  });

  useEffect(() => {
    if (!isEditMode && formik.values.amount !== undefined) {
      const subtotal = formik.values.amount || 0;
      const gstAmount =
        subtotal === 0 ? 0 : subtotal * (gstPercentage / 100);
      formik.setFieldValue("sgst", subtotal === 0 ? 0 : gstAmount / 2);
      formik.setFieldValue("cgst", subtotal === 0 ? 0 : gstAmount / 2);
      formik.setFieldValue("totalAmount", subtotal + gstAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.amount, gstPercentage, isEditMode]);

  const handelCloseModalState = (updatedInvoice) => {
    // Ignore UI events from BasicModal / Cancel (only pass saved invoice objects)
    const savedInvoice =
      updatedInvoice &&
      typeof updatedInvoice === "object" &&
      updatedInvoice.id != null &&
      !updatedInvoice.nativeEvent
        ? updatedInvoice
        : undefined;
    onClose?.(savedInvoice);
    formik.resetForm();
    setAttachment(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setAttachment(file);
    formik.setFieldValue("attachment", file?.name || "");
  };

  const handleDeleteAttachment = () => {
    setAttachment(null);
    formik.setFieldValue("attachment", "");
  };

  const applyAmountChange = (rawValue) => {
    if (isEditMode) return;
    const amount = Number(rawValue) || 0;
    formik.setFieldValue("amount", amount);
    const { sgst, cgst, totalAmount } = calcInvoiceGstBreakdown(
      amount,
      gstPercentage
    );
    formik.setFieldValue("sgst", sgst);
    formik.setFieldValue("cgst", cgst);
    formik.setFieldValue("totalAmount", totalAmount);
  };

  return (
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      title={modalTitle}
      sx={INVOICE_FORM_MODAL_SX}
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={["DONATION", "MISC"]}
                value={formik.values.invoiceType || null}
                onChange={(event, newValue) => {
                  if (!isEditMode) {
                    formik.setFieldValue("invoiceType", newValue || "");
                  }
                }}
                disabled={isEditMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...DENSE_FIELD_PROPS}
                    variant="outlined"
                    label="Invoice Type"
                    error={
                      formik.touched.invoiceType &&
                      Boolean(formik.errors.invoiceType)
                    }
                    helperText={
                      formik.touched.invoiceType && formik.errors.invoiceType
                    }
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="fromName"
                name="fromName"
                label="From Name"
                value={formik.values.fromName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isEditMode}
                error={formik.touched.fromName && Boolean(formik.errors.fromName)}
                helperText={formik.touched.fromName && formik.errors.fromName}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="amount"
                name="amount"
                label="Amount (₹)"
                type="number"
                value={formik.values.amount}
                onChange={(e) => applyAmountChange(e.target.value)}
                disabled={isEditMode}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                inputProps={{ min: 0, step: 0.01 }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12}>
              <InvoiceSummarySection
                subtotal={formik.values.amount}
                gstPercentage={gstPercentage}
                totalAmount={formik.values.totalAmount}
                muted={isEditMode}
                paymentModeValue={formik.values.payment_mode}
                onPaymentModeChange={(value) => {
                  if (!isEditMode) {
                    formik.setFieldValue("payment_mode", value);
                  }
                }}
                paymentError={
                  !isEditMode &&
                  formik.touched.payment_mode &&
                  Boolean(formik.errors.payment_mode)
                }
                paymentHelperText={
                  !isEditMode &&
                  formik.touched.payment_mode &&
                  formik.errors.payment_mode
                }
                paymentDisabled={isEditMode}
                attachmentField={
                  <InvoiceAttachmentControl
                    attachmentName={formik.values.attachment}
                    attachmentUrl={
                      isEditMode
                        ? resolveAttachmentUrl(invoice_info?.attachment) ||
                          resolveAttachmentUrl(formik.values.attachment)
                        : ""
                    }
                    attachmentFile={attachment}
                    onFileChange={handleFileChange}
                    onDelete={handleDeleteAttachment}
                    disabled={isEditMode}
                  />
                }
                remarksName="particulars"
                remarksValue={formik.values.particulars}
                onRemarksChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={handelCloseModalState}
                submitType="submit"
                submitDisabled={
                  formik.isSubmitting ||
                  (!isEditMode &&
                    (!formik.values.payment_mode?.mode ||
                      (formik.values.payment_mode?.mode === "credit" &&
                        !formik.values.payment_mode?.organizationId)))
                }
                loading={formik.isSubmitting}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default AddNewInvoice;
